import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import Form from './Form';
import Notification from '@deporvillage/notification-component';
import { I18n } from '@deporvillage/utils-library-component';

import styles from './RestorePassword.module.scss';

const Events = {
    LinkForgotPassword: 'login-link-forgot',
};

/* istanbul ignore next */
const scrollToBottom = (elem) =>
    elem.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'end' });

const ForgotPasswordDispatcher = ({ onClick }) => {
    const handleOnForgotDispatcherClick = (e) => {
        e.preventDefault();
        onClick && onClick(Events.LinkForgotPassword);
    };
    return (
        <>
            <p className={styles['valid-token-instruccions']}>
                <I18n>restorepassword.restorepassword_popup.token_instructions</I18n>
            </p>
            <button onClick={handleOnForgotDispatcherClick} className={styles['button']}>
                <I18n>restorepassword.restorepassword_popup.title.forgotpassword</I18n>
            </button>
        </>
    );
};

ForgotPasswordDispatcher.propTypes = {
    onClick: PropTypes.func,
};

const RestorePasswordForm = ({ customerData, onClick, onChange, error }) => {
    const { password, passwordConfirmation } = customerData;

    const restorePasswordError = React.useRef(null);
    const handleOnChange = (input) => onChange && onChange(input, customerData);

    const handleOnRestorePassword = (e) => {
        e.preventDefault();
        onClick && onClick(customerData);
    };

    React.useEffect(() => {
        if (error && restorePasswordError.current) {
            scrollToBottom(restorePasswordError.current);
        }
    }, [error]);

    return (
        <>
            <div className={styles['title-form']}>
                <h1>
                    <I18n>restorepassword.restorepassword_popup.title.restorepassword</I18n>
                </h1>
            </div>
            <form
                autoComplete={'off'}
                className={styles['form-component']}
                id={'restorepassword-form'}
            >
                <p>
                    <I18n>restorepassword.restorepassword_popup.how</I18n>
                </p>
                <Form {...password} onChange={(value) => handleOnChange({ ...password, value })} />
                <Form
                    {...passwordConfirmation}
                    onChange={(value) => handleOnChange({ ...passwordConfirmation, value })}
                />
                <div className={`${styles['minimum-character-info']}`}>
                    <span className={`${styles['icon']} icon icon-info-outline`} />
                    <span>
                        <I18n>form.generic.minimum_character</I18n>
                    </span>
                </div>
                <button onClick={handleOnRestorePassword} className={styles['button']}>
                    <I18n>restorepassword.restorepassword_popup.back_to_login</I18n>
                </button>

                <div className={styles['required']}>
                    * <I18n>form.generic.required_fields</I18n>
                </div>
            </form>
            {error ? (
                <div ref={restorePasswordError} className={styles['restorepassword-error']}>
                    <Notification
                        message={<I18n>restorepassword.restorepassword_popup.error</I18n>}
                        type='error'
                    />
                </div>
            ) : null}
        </>
    );
};

RestorePasswordForm.prototypes = {
    customerData: PropTypes.shape({
        password: PropTypes.shape({
            id: PropTypes.string,
            type: PropTypes.string,
            label: PropTypes.string,
            value: PropTypes.string,
            error: PropTypes.string,
            validate: PropTypes.array,
        }),
        passwordConfirmation: PropTypes.shape({
            id: PropTypes.string,
            type: PropTypes.string,
            label: PropTypes.string,
            value: PropTypes.string,
            error: PropTypes.string,
            validate: PropTypes.array,
        }),
    }),
    onClick: PropTypes.func,
    onChange: PropTypes.func,
    error: PropTypes.string,
};

const RestorePassword = ({
    customerData,
    onChange,
    onRestorePassword,
    onClick,
    error,
    validToken,
}) => {
    const handleOnChange = (input) => onChange && onChange(input, customerData);

    const handleOnRestorePassword = () => {
        onRestorePassword && onRestorePassword(customerData);
    };

    const handleOnClick = (e) => onClick && onClick(e);

    return (
        <div className={styles['restorepassword-component']}>
            {validToken ? (
                <RestorePasswordForm
                    customerData={customerData}
                    onChange={handleOnChange}
                    onClick={handleOnRestorePassword}
                    error={error}
                />
            ) : (
                <>
                    <div className={styles['title-form']}>
                        <h1>
                            <I18n>restorepassword.restorepassword_popup.title.token_expired</I18n>
                        </h1>
                    </div>
                    <ForgotPasswordDispatcher onClick={handleOnClick} />
                </>
            )}
        </div>
    );
};

RestorePassword.propTypes = {
    customerData: PropTypes.shape({
        password: PropTypes.shape({
            id: PropTypes.string,
            type: PropTypes.string,
            label: PropTypes.string,
            value: PropTypes.string,
            error: PropTypes.string,
            validate: PropTypes.array,
        }),
        passwordConfirmation: PropTypes.shape({
            id: PropTypes.string,
            type: PropTypes.string,
            label: PropTypes.string,
            value: PropTypes.string,
            error: PropTypes.string,
            validate: PropTypes.array,
        }),
    }),
    onRestorePassword: PropTypes.func,
    onChange: PropTypes.func,
    onClick: PropTypes.func,
    error: PropTypes.string,
    validToken: PropTypes.bool,
};

export default RestorePassword;
